import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { fetchAdminUsers } from '@/lib/queries';
import { useCan } from '@/hooks/use-scopes';
import { humanize } from '@/lib/format';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import {
  TAXONOMY_TEMPLATES,
  CHANNELS_DEFAULT,
  type Vertical,
  type Channel,
  VERTICALS,
} from '@shared/enums';
import { qk } from '@/lib/queries';

export default function ClientCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [vertical, setVertical] = useState<Vertical>('clinic');
  const [contactEmail, setContactEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [channels, setChannels] = useState<Channel[]>(['instagram', 'tiktok']);

  const template = useMemo(() => TAXONOMY_TEMPLATES[vertical], [vertical]);

  const [primaryLabel, setPrimaryLabel] = useState(template.primary.label);
  const [primaryValues, setPrimaryValues] = useState<string>(template.primary.sampleValues.join(', '));
  const [secondaryLabel, setSecondaryLabel] = useState(template.secondary.label);
  const [secondaryValues, setSecondaryValues] = useState<string>(template.secondary.sampleValues.join(', '));
  const [talentLabel, setTalentLabel] = useState(template.talent.label);
  const [talentEnabled, setTalentEnabled] = useState(template.talent.enabled);

  function applyTemplate(v: Vertical) {
    setVertical(v);
    const t = TAXONOMY_TEMPLATES[v];
    setPrimaryLabel(t.primary.label);
    setPrimaryValues(t.primary.sampleValues.join(', '));
    setSecondaryLabel(t.secondary.label);
    setSecondaryValues(t.secondary.sampleValues.join(', '));
    setTalentLabel(t.talent.label);
    setTalentEnabled(t.talent.enabled);
  }

  // Who is on this client from day one. Pre-ticked: the default crew. Only
  // people allowed to set clinic access get to choose; otherwise the server
  // applies the default crew on its own.
  const canScope = useCan('users.scope');
  const staff = useQuery({ queryKey: ['admin-users', 'team-pick'], queryFn: () => fetchAdminUsers({ active: true }), enabled: canScope });
  const [team, setTeam] = useState<string[] | null>(null);
  useEffect(() => {
    if (team === null && staff.data) setTeam(staff.data.filter((u) => u.default_crew && !u.unrestricted && u.role !== 'client').map((u) => u.id));
  }, [staff.data, team]);
  const pickable = (staff.data ?? []).filter((u) => u.role !== 'client' && !u.unrestricted);
  const toggleMember = (id: string) => setTeam((cur) => (cur ?? []).includes(id) ? (cur ?? []).filter((x) => x !== id) : [...(cur ?? []), id]);

  const create = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Name is required');
      const r = await api.post<{ id: string }>('/clients', {
        team_user_ids: canScope ? (team ?? []) : undefined,
        name: name.trim(),
        contact_email: contactEmail.trim() || null,
        phone: phone.trim() || null,
        vertical,
        taxonomy: {
          primary:   { label: primaryLabel.trim(),   values: csvToList(primaryValues) },
          secondary: { label: secondaryLabel.trim(), values: csvToList(secondaryValues) },
          talent:    { label: talentLabel.trim(),    enabled: talentEnabled },
        },
        talents: [],
        default_channels: channels,
      });
      return r.data.id;
    },
    onSuccess: (id) => {
      toast.success('Client created');
      qc.invalidateQueries({ queryKey: qk.clients });
      navigate(`/clients/${id}`);
    },
  });

  function toggleChannel(c: Channel) {
    setChannels((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]));
  }

  return (
    <div className="max-w-3xl">
      <PageHeader title="New client" subtitle="Pick a vertical template; tweak labels + values to match the brand" />

      <Card className="space-y-5">
        <Input label="Client name" placeholder="e.g. Cocoona Center for Aesthetic Transformation" value={name} onChange={(e) => setName(e.target.value)} required />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Contact email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
          <Input label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>

        <div className="space-y-2">
          <label className="text-xs text-muted">Vertical (taxonomy template)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {VERTICALS.map((v) => (
              <button
                key={v} type="button" onClick={() => applyTemplate(v)}
                className={
                  'text-left text-xs px-3 py-2 rounded-lg border transition ' +
                  (vertical === v
                    ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                    : 'border-slate-800 bg-bg hover:bg-bg-card text-muted-strong')
                }
              >
                {TAXONOMY_TEMPLATES[v].label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4 pt-2 border-t border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Primary tag label" value={primaryLabel} onChange={(e) => setPrimaryLabel(e.target.value)} />
            <Input label="Primary values (comma-separated)" value={primaryValues} onChange={(e) => setPrimaryValues(e.target.value)} hint="e.g. Dental, Dermatology" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Secondary tag label" value={secondaryLabel} onChange={(e) => setSecondaryLabel(e.target.value)} />
            <Input label="Secondary values (comma-separated)" value={secondaryValues} onChange={(e) => setSecondaryValues(e.target.value)} hint="e.g. veneer, botox" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <Input label="Talent label" value={talentLabel} onChange={(e) => setTalentLabel(e.target.value)} disabled={!talentEnabled} />
            <label className="flex items-center gap-2 text-sm text-muted-strong">
              <input type="checkbox" checked={talentEnabled} onChange={(e) => setTalentEnabled(e.target.checked)} className="accent-accent-400" />
              Track talent for this client
            </label>
          </div>
        </div>

        <div className="space-y-2 pt-2 border-t border-slate-800">
          <label className="text-xs text-muted">Default distribution channels</label>
          <div className="flex flex-wrap gap-2">
            {CHANNELS_DEFAULT.map((c) => (
              <button
                key={c} type="button" onClick={() => toggleChannel(c)}
                className={
                  'text-xs px-3 py-1.5 rounded-full border transition ' +
                  (channels.includes(c)
                    ? 'border-accent-400 bg-accent-600/20 text-accent-400'
                    : 'border-slate-800 text-muted hover:text-muted-strong')
                }
              >
                {c.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {canScope && (
          <div className="space-y-2 pt-2 border-t border-slate-800" data-testid="team-picker">
            <label className="text-xs text-muted">Team on this client from day one</label>
            <p className="text-[11px] text-muted">Pre-ticked: the default crew. Each person keeps their own level (clinic-wide or own tasks). Owner and managers see every clinic and are not listed.</p>
            {pickable.length === 0 && <p className="text-xs text-muted">No staff yet.</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {pickable.map((u) => (
                <label key={u.id} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-800/50 cursor-pointer">
                  <input type="checkbox" checked={(team ?? []).includes(u.id)} onChange={() => toggleMember(u.id)} className="accent-accent-400" />
                  <span className="text-sm text-muted-strong">{u.display_name}</span>
                  <span className="text-[11px] text-muted ml-auto">
                    {humanize(u.designations[0] ?? u.role)} · {u.access_level === 'clients' ? 'clinic-wide' : 'own tasks'}
                    {u.default_crew ? ' · default crew' : ''}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => navigate('/clients')}>Cancel</Button>
          <Button onClick={() => create.mutate()} loading={create.isPending}>Create client</Button>
        </div>
      </Card>
    </div>
  );
}

function csvToList(s: string): string[] {
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}
